import type React from "react";
import type { CustomDomain } from "../types/index.js";

interface CustomDomainManagerProps {
  domains: CustomDomain[];
  onRecheckVerification: (domainId: string) => void;
  onToggleDomainStatus: (domainId: string, currentStatus: string) => void;
}

export const CustomDomainManager: React.FC<CustomDomainManagerProps> = ({
  domains,
  onRecheckVerification,
  onToggleDomainStatus,
}) => {
  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "16px", color: "#f8fafc" }}>
            Cloudflare for SaaS — Custom Vanity Domains
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#64748b" }}>
            Edge ingress routing maps custom tenant hostnames to tenant_id. SSL certificates provisioned automatically via Cloudflare Edge.
          </p>
        </div>
        <div style={{ fontSize: "12px", color: "#94a3b8" }}>
          Ingress Target: <code style={{ color: "#38bdf8" }}>ingress.downline.network</code>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#0f172a",
          border: "1px solid #1e293b",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            textAlign: "left",
            fontSize: "13px",
            color: "#e2e8f0",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#1e293b",
                color: "#94a3b8",
                borderBottom: "1px solid #334155",
              }}
            >
              <th style={{ padding: "12px 16px" }}>Custom Hostname</th>
              <th style={{ padding: "12px 16px" }}>Tenant UUID</th>
              <th style={{ padding: "12px 16px" }}>Domain Status</th>
              <th style={{ padding: "12px 16px" }}>Edge SSL Status</th>
              <th style={{ padding: "12px 16px" }}>DNS Verification Target</th>
              <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {domains.map((domain) => {
              const isVerified = domain.status === "active";
              const isSslActive = domain.sslStatus === "active";

              return (
                <tr
                  key={domain.id}
                  style={{
                    borderBottom: "1px solid #1e293b",
                    backgroundColor: domain.status === "suspended" ? "#1e1014" : "transparent",
                  }}
                >
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: "#38bdf8" }}>
                    {domain.hostname}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <code
                      style={{
                        backgroundColor: "#1e293b",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontSize: "11px",
                        color: "#94a3b8",
                      }}
                    >
                      {domain.tenantId.slice(0, 8)}...
                    </code>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "9999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor: isVerified ? "#064e3b" : "#78350f",
                        color: isVerified ? "#34d399" : "#fbbf24",
                      }}
                    >
                      {domain.status}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "9999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor: isSslActive ? "#064e3b" : "#1e293b",
                        color: isSslActive ? "#34d399" : "#a78bfa",
                      }}
                    >
                      {domain.sslStatus}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {isVerified ? (
                      <span style={{ color: "#10b981", fontSize: "12px" }}>
                        ✓ CNAME → ingress.downline.network
                      </span>
                    ) : (
                      <div style={{ fontSize: "11px", color: "#fbbf24" }}>
                        <div>CNAME: <code>ingress.downline.network</code></div>
                        {domain.verificationTxtValue && (
                          <div style={{ marginTop: "2px" }}>
                            TXT: <code>{domain.verificationTxtValue.slice(0, 16)}...</code>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => onRecheckVerification(domain.id)}
                      style={{
                        padding: "4px 8px",
                        marginRight: "6px",
                        fontSize: "12px",
                        backgroundColor: "#1e293b",
                        color: "#38bdf8",
                        border: "1px solid #334155",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Recheck
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleDomainStatus(domain.id, domain.status)}
                      style={{
                        padding: "4px 8px",
                        fontSize: "12px",
                        backgroundColor: domain.status === "active" ? "#450a0a" : "#064e3b",
                        color: domain.status === "active" ? "#f87171" : "#34d399",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {domain.status === "active" ? "Suspend" : "Activate"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
